import React, { useMemo } from 'react';
import { AgGridTable } from 'components/common';
import ProjectStatusRenderer from 'components/app/crm/project/component/ProjectStatusRenderer';
import { formatNumber } from 'helpers/utils';
import PropTypes from 'prop-types';

// AgGridTable's defaultColDef is `flex: 1`, which makes AG Grid ignore `width`
// and give every column an equal share — long values (pipeline stage, sales
// unit) end up clipped. `flex: 0` opts a column back into its own width.
const fixed = (width) => ({ width, minWidth: width, flex: 0 });

const contactLabel = (contact) =>
  contact?.id ? [contact.name, `#${contact.id}`].filter(Boolean).join(' ') : '';

const OpportunityTable = ({data}) => {
  const projectUrl = "/crm/projects";

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'PCV #',
      ...fixed(120),
      filter: 'agTextColumnFilter',
      valueGetter: params => params.data.pcv_number || '',
      cellRenderer: params => (
        <a href={`${projectUrl}/${params.data.id}`} target="_blank" rel="noreferrer" className="tw-text-blue-600">
          {params.data.pcv_number || `#${params.data.id}`}
        </a>
      )
    },
    {
      field: 'name',
      headerName: 'Project name',
      flex: 2,
      minWidth: 210,
      filter: 'agTextColumnFilter',
      tooltipValueGetter: params => params.data.name || '',
      cellRenderer: params => (
        <a href={`${projectUrl}/${params.data.id}`} target="_blank" rel="noreferrer" className="tw-font-semibold tw-text-blue-600">
          {params.data.name}
        </a>
      )
    },
    {
      field: 'q_number',
      headerName: 'Quote Number',
      ...fixed(130),
      filter: 'agTextColumnFilter'
    },
    {
      field: 'j_number',
      headerName: 'P/J Number',
      ...fixed(125),
      filter: 'agTextColumnFilter'
    },
    {
      field: 'weight',
      headerName: 'Weight',
      ...fixed(110),
      filter: 'agNumberColumnFilter',
      cellRenderer: params => params.data.weight ? `${formatNumber(params.data.weight)} M.T` : '—'
    },
    {
      field: 'amount',
      headerName: 'Amount',
      ...fixed(130),
      filter: 'agNumberColumnFilter',
      cellRenderer: params => params.data.amount ? `$${formatNumber(params.data.amount)}` : '—'
    },
    {
      // Sales pipeline stage — same badge the project list uses, so the tab
      // reads the same as the Overview pipeline chart. Wide enough for the
      // longest stage name ("Proposal preparation & submission").
      field: 'sales_pipeline',
      headerName: 'Status',
      ...fixed(200),
      filter: 'agTextColumnFilter',
      valueGetter: params => params.data.sales_pipeline?.name || '',
      tooltipValueGetter: params => params.data.sales_pipeline?.name || '',
      cellRenderer: params => params.data.sales_pipeline?.name
        ? <ProjectStatusRenderer value={params.data.sales_pipeline.name} data={params.data} />
        : '—'
    },
    {
      field: 'sales_incharge',
      headerName: 'Sales incharge',
      ...fixed(130),
      filter: 'agTextColumnFilter'
    },
    {
      field: 'sales_unit',
      headerName: 'Sales unit',
      ...fixed(190),
      filter: 'agTextColumnFilter',
      valueGetter: params => params.data.sales_unit?.full_name || '',
      cellRenderer: params => params.data.sales_unit?.full_name || '—'
    },
    {
      // Full contact name + ID — the bare ID alone said nothing about who it is.
      field: 'contact',
      headerName: 'Contact',
      flex: 1,
      minWidth: 190,
      filter: 'agTextColumnFilter',
      valueGetter: params => contactLabel(params.data.contact),
      tooltipValueGetter: params => contactLabel(params.data.contact),
      cellRenderer: params => {
        const contact = params.data.contact;
        if (!contact?.id) return <span className="tw-text-red-600">No Contact</span>;
        return (
          <a href={`/crm/contacts/${contact.id}`} target="_blank" rel="noreferrer" className="tw-text-blue-600">
            {contact.name || 'Unnamed contact'}
            <span className="tw-text-slate-400 tw-ml-1">#{contact.id}</span>
          </a>
        );
      }
    },
    {
      field: 'created_by',
      headerName: 'Created by',
      ...fixed(120),
      filter: 'agTextColumnFilter'
    },
    {
      field: 'created_date',
      headerName: 'Created date',
      ...fixed(185),
      filter: 'agTextColumnFilter'
    }
  ], [projectUrl]);

  if (!data) {
    return null;
  }

  return (
    <AgGridTable
      columnDefs={columns}
      rowData={data}
      height="400px"
      sort
      filter
    />
  );
};

OpportunityTable.propTypes = {
  data: PropTypes.array.isRequired
};

export default OpportunityTable;